/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import * as url from 'url';
import urlUtil from './Url';
import getConfig from '../config';
import { getEnvironment } from '../utils/Environment';
import { getLocalizedURL } from '@intl/adobe-locales';

const ENTERING_URL = 'enteringURL';
const ENTERING_TAB = 'enteringTab';
const allowListContextParam = ['nat_geo'];

export default class RedirectionUtils {
	/**
	 * Returns true if the URL has the 'context' query param.
	 * @param {string} inputUrl
	 */
	static hasContextQueryParam(inputUrl: string) {
		let hasParam = false;
		if (inputUrl) {
			const urlParams: any = url.parse(inputUrl, true).query;
			if (urlParams && urlParams.context && allowListContextParam.indexOf(urlParams.context) === -1) {
				hasParam = true;
			}
		}
		return hasParam;
	}

	/**
	 * Save the entering URL to Session Storage
	 */
	static setEnteringURL() {
		if (window.cchome.util.storageAvailable('sessionStorage')) {
			sessionStorage.setItem(ENTERING_URL, JSON.stringify(window.location.href));
		}
	}

	/**
	 * Get the entering URL to Session Storage
	 */
	static getEnteringURL() {
		if (window.cchome.util.storageAvailable('sessionStorage') && sessionStorage.getItem(ENTERING_URL) !== null) {
			const enteringUrl = sessionStorage.getItem(ENTERING_URL);
			if (enteringUrl !== null) {
				return JSON.parse(enteringUrl);
			}
		}
		return null;
	}

	/**
	 * Save the entering Tab to Session Storage
	 */
	static setEnteringTab(tabName) {
		if (window.cchome.util.storageAvailable('sessionStorage')) {
			const enteringTab = sessionStorage.getItem(ENTERING_TAB);
			if (!enteringTab || !JSON.parse(enteringTab)) {
				sessionStorage.setItem(ENTERING_TAB, JSON.stringify(tabName));
			}
		}
	}

	/**
	 * Get the entering Tab to Session Storage
	 */
	static getEnteringTab() {
		if (window.cchome.util.storageAvailable('sessionStorage')) {
			const enteringTab = sessionStorage.getItem(ENTERING_TAB);
			if (enteringTab) {
				return JSON.parse(enteringTab);
			}
		}
		return null;
	}

	/**
	 * This method returns the URL CC Home should redirect the user to, if they are not eligible.
	 * It checks for the presence of a 'context' query parameter in the input url and if present, uses it to figure out the redirect URL.
	 */
	static getAdobeRedirectURL(inputUrl: string, lang: string) {
		let language = lang || 'en';
		// Users whose IMS country code is set to China are deemed not eligible for CC Home.
		// They are redirected to the appropriate pages in the China locale.
		const imsProfile = window.adobeIMS.getUserProfile();
		if (imsProfile && imsProfile.countryCode && imsProfile.countryCode.toLowerCase() === 'cn') {
			language = 'zh-Hans';
		}

		let redirectUrl = getLocalizedURL(getConfig('ADOBE_DOT_COM_CC_APPS'), language);
		if (inputUrl) {
			const urlParams: any = url.parse(inputUrl, true).query;
			if (urlParams && urlParams.context) {
				switch (urlParams.context) {
					case 'home_cc':
						redirectUrl = getLocalizedURL(getConfig('ADOBE_DOT_COM_CC_HOME'), language);
						break;
					case 'apps_cc':
						redirectUrl = getLocalizedURL(getConfig('ADOBE_DOT_COM_CC_APPS'), language);
						break;
					default:
						redirectUrl = getLocalizedURL(getConfig('ADOBE_DOT_COM_CC_APPS'), language);
				}
			}
		}

		return redirectUrl;
	}

	/**
	 * Removes the 'context' query parameter from the URL, if present and returns the path component of the URL. It preserves any other query params.
	 * @return {string} URL path without the context parameter.
	 */
	static returnPathWithoutContextParam(inputUrl: string) {
		let path = '';
		if (!inputUrl) {
			path = '/';
		} else {
			const parsedUrl = url.parse(inputUrl, true);
			const urlParams = parsedUrl.query;
			const filteredParams = Object.entries(urlParams)
				.map((pair) => {
					let queryParamPair = '';
					if (pair[0] !== 'context') {
						queryParamPair = `${pair[0]}=${pair[1]}`;
					}
					return queryParamPair;
				})
				.filter((item) => item !== '')
				.join('&');

			const urlWithoutContext = `${inputUrl.split('?')[0]}${filteredParams ? `?${filteredParams}` : ''}`;
			path = url.parse(urlWithoutContext).path || '';
		}
		return path;
	}

	static redirectLoggedOut(language: string) {
		const isHomePath = () => {
			const path = window.location.pathname.split('/');
			return (path.length === 2 && path[1] === 'cc') || (path.length > 2 && path[1] === 'cc' && path[2] === '');
		};

		const isGrowthPath = () => {
			const path = window.location.pathname.split('/');
			return path.length > 1 && path[1] === 'ccg';
		};

		const isAppsPath = () => {
			const path = window.location.pathname.split('/');
			return path.length > 1 && path[1] === 'apps';
		};

		const isLearnPath = () => {
			const path = window.location.pathname.split('/');
			return path.length > 1 && path[1] === 'learn';
		};

		const isGoURLViewApps = () =>
			// https://creativecloud.adobe.com/apps/?context=apps_cc
			urlUtil.getParam('context') === 'apps_cc';
		const isNoCCRedirect = () =>
			// https://creativecloud.adobe.com/?context=no_cc_redirect
			urlUtil.getParam('context') === 'no_cc_redirect';
		const isACOMRedirect = () =>
			// https://creativecloud.adobe.com/?promoid=29NMCNT6&mv=other
			urlUtil.getParam('promoid') === '29NMCNT6';
		const isDiscoverPath = () => {
			const path = window.location.pathname.split('/');
			return path.length > 2 && path[2] === 'discover';
		};

		const isLibrariesPath = () => {
			const path = window.location.pathname.split('/');
			return path.length > 2 && path[2] === 'libraries';
		};

		const excludedPathFromRedirect =
			isHomePath() ||
			isGrowthPath() ||
			isAppsPath() ||
			isLearnPath() ||
			isGoURLViewApps() ||
			isNoCCRedirect() ||
			isACOMRedirect();

		// Trailhead redirection requirement: https://trello.com/c/O4AAfFVR
		// /apps/download doesn't need to be excluded here due to separate bundle & is managed by Lambda
		if (!excludedPathFromRedirect) {
			RedirectionUtils.setEnteringURL();

			if (isDiscoverPath() || isLibrariesPath()) {
				const pages = ['/discover', '/libraries'];
				for (let index = 0; index < pages.length; index++) {
					if (window.location.href.indexOf(pages[index]) > -1) {
						const fullPath = window.location.href.split(`${pages[index]}/`);

						if (fullPath && fullPath.length > 1) {
							if (language === 'en') {
								window.location.assign(`${window.location.origin}${pages[index]}/${fullPath[1]}`);
							} else {
								window.location.assign(
									`${window.location.origin}/${language}${pages[index]}/${fullPath[1]}`,
								);
							}
						} else {
							if (language === 'en') {
								window.location.assign(`${window.location.origin}${pages[index]}`);
							} else {
								window.location.assign(`${window.location.origin}/${language}${pages[index]}`);
							}
						}
						break;
					}
				}
			} else {
				if (language === 'en') {
					window.location.assign(`${window.location.origin}/`);
				} else {
					window.location.assign(`${window.location.origin}/${language}`);
				}
			}
		} else {
			window.adobeIMS && window.adobeIMS.signIn();
		}
	}

	// Redirect <lang>/<extPath> to /cc/<extPath>
	// Only affect Home, Discover or Libraries paths
	// Example https://creativecloud.adobe.com/es/discover/article/placefingerhere-challenge
	// will be redirected to https://creativecloud.adobe.com/cc/discover/article/placefingerhere-challenge
	static redirectLoggedIn() {
		let localeInPath = '';
		const path = window.location.pathname.split('/');

		const isLoggedOutPath = () => {
			// No Locale In Path found, must be English
			if (path[1] === '' || path[1] === 'discover' || path[1] === 'libraries') {
				return true;
			}
			// Check if there is a locale in Path, if true, must be non-EN logged out
			const supportedLanguage = Object.keys(getConfig('SUPPORTED_LANGUAGES'));
			const checkLocale = path.length > 1 && path[1];
			if (checkLocale && supportedLanguage.indexOf(checkLocale) > -1) {
				localeInPath = checkLocale;
				return true;
			}
			return false;
		};

		if (isLoggedOutPath()) {
			let redirectUrl = window.location.href;
			if (!localeInPath) {
				// EN case
				if (getEnvironment() === 'local') {
					redirectUrl = window.location.href.replace(
						'dev.hollywoodstudios.corp.adobe.com:9000',
						'dev.hollywoodstudios.corp.adobe.com:9000/cc',
					);
				} else {
					redirectUrl = window.location.href.replace(
						`creativecloud.adobe.com/`,
						'creativecloud.adobe.com/cc/',
					);
				}
			} else {
				// Non-EN cases
				redirectUrl = window.location.href.replace(
					`creativecloud.adobe.com/${localeInPath}`,
					'creativecloud.adobe.com/cc',
				);
			}
			window.location.assign(redirectUrl);
		}
	}
}
